import { useAppContext } from "../../context/AppContext";
import { dateDifference, formatAmount } from "../../utils";
import { useState } from "react";
import { IStakeResponse, ITraderResponse } from "../../utils/types";
import { useTheme } from "../../context/ThemeContext";
import { ChevronLeftIcon, ChevronRightIcon } from "../SVGIcons";
import { Link } from "react-router-dom";
import { useEVMContext } from "../../context/EVMContext";
const TraderStakesHistory: React.FC<{
  traderData: ITraderResponse;
  setIsUnstakeConfirmModalOpen: (isOpen: boolean) => void;
  setActiveStake: (stake: IStakeResponse | null) => void;  
  traderAddress?: string;
}> = ({ traderData, setIsUnstakeConfirmModalOpen, setActiveStake, traderAddress }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const { accounts } = useEVMContext();
  const { theme } = useTheme();

  const sortedStakes = traderData?.stakes
    ?.sort((a: IStakeResponse, b: IStakeResponse) => {
      // Staked stakes (unstaked === false) first
      if (a.unstaked && !b.unstaked) return 1;
      if (!a.unstaked && b.unstaked) return -1;
      // If both are the same, sort by date (desc)
      const dateA = new Date(a?.updated_at || a?.created_at).getTime();
      const dateB = new Date(b?.updated_at || b?.created_at).getTime();
      return dateB - dateA;
    }) || [];

  const totalPages = Math.ceil(sortedStakes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStakes = sortedStakes.slice(startIndex, endIndex);

  const handleItemsPerPageChange = (newSize: number) => {
    setItemsPerPage(newSize);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const unstakeAllClickEvent = (stake: IStakeResponse) => {
    setActiveStake(stake)
    setIsUnstakeConfirmModalOpen(true);
  }

  return <div className="relative transition-colors duration-300">
    <div
      className="absolute inset-0 rounded-[24px] pointer-events-none"
      style={{
        padding: '1px',
        background: `linear-gradient(to bottom, ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)'}, ${theme === 'dark' ? 'rgba(255,255,255,0)' : 'rgba(0,0,0,0)'}`,
        WebkitMask:
          'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        zIndex: 10,
      }}
    ></div>
    <h2 className="text-xl text-black dark:text-white font-borna px-6 py-4">Stakes</h2>

    {sortedStakes.length === 0 ? (
      <div className="flex items-center justify-center py-8">
        <p className="text-gray-500 dark:text-gray-400 text-lg font-borna">No Stakes History</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[rgba(0,0,0,0.06)] dark:divide-white/20">
          <thead className="">
            <tr>
              {/* <th className="px-6 py-3 text-left text-xs font-medium font-borna text-black/50 dark:text-white/50 tracking-wider">Time</th> */}
              <th className="px-6 py-3 text-left text-xs font-medium font-borna text-black/50 dark:text-white/50 tracking-wider">Subnet</th>
              <th className="px-6 py-3 text-left text-xs font-medium font-borna text-black/50 dark:text-white/50 tracking-wider">Staked Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium font-borna text-black/50 dark:text-white/50 tracking-wider">Current Stakes</th>
              <th className="px-6 py-3 text-left text-xs font-medium font-borna text-black/50 dark:text-white/50 tracking-wider">Staked Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium font-borna text-black/50 dark:text-white/50 tracking-wider">Alpha Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium font-borna text-black/50 dark:text-white/50 tracking-wider">Staked</th>
              <th className="px-6 py-3 text-left text-xs font-medium font-borna text-black/50 dark:text-white/50 tracking-wider">Profit/Loss</th>
              <th className="px-6 py-3 text-left text-xs font-medium font-borna text-black/50 dark:text-white/50 tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="">
            {currentStakes.map((stake: any, index: any) => (
              <tr key={index} className={`font-borna hover:bg-[#F2F2F2] dark:hover:bg-[#262626]`}>
                {/* <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">{dateDifference(stake?.updated_at || stake?.created_at)} ago</td> */}
                <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                  <Link to={`/subnet/${stake?.subnet}`} className="text-blue-500 underline hover:text-blue-700">
                    {stake?.subnet}
                  </Link>
                </td>
                <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">{stake?.unstaked ? 0 : formatAmount(stake?.staked)}τ</td>
                <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">{formatAmount(stake?.current_stake_balance)}τ</td>
                <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">{formatAmount(String(stake?.alpha_amount > 0 ? (stake?.staked / stake?.alpha_amount || 0) : 0), true)}</td>
                <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">{formatAmount(stake?.alpha_amount)}</td>
                <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">{stake?.unstaked ? 'False' : 'True'}</td>
                <td className={`px-6 py-2 whitespace-nowrap text-sm ${stake?.staked <= 0 || !stake?.unstaked ? 'text-green-600' : 'text-red-600'}`}>{stake?.unstaked ? Number((0 - Number(formatAmount(stake?.staked))).toFixed(3)).toString() : 0}τ</td>
                <td className={`px-6 py-2 whitespace-nowrap text-sm`}>
                  {!stake?.unstaked && accounts[0] && (traderAddress ? accounts[0] == traderAddress : true) && (
                    <button
                      className="px-2 py-1 text-green-500 underline"
                      onClick={() => unstakeAllClickEvent(stake)}
                    >
                      Unstake
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {totalPages > 1 && (
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-3 sm:gap-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-auto">
          <span className="text-sm text-black/50 dark:text-white/50">
            Showing {startIndex + 1} to {Math.min(endIndex, sortedStakes.length)} of {sortedStakes.length} entries
          </span>
          <div className="flex items-center space-x-2 w-full sm:w-auto">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className={`px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-20 ${theme === 'dark' ? 'dark' : ''}`}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 px-4 sm:px-6 space-y-3 sm:space-y-0">
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-1 py-1 border text-black/70 dark:text-white/70 border-black/50 dark:border-white/50 rounded-[100%] disabled:opacity-50"
            >
              <ChevronLeftIcon fillColor={currentPage === 1 ? 'gray' : theme === 'dark' ? 'white' : 'black'} />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-1 py-1 border text-black/70 dark:text-white/70 border-black/50 dark:border-white/50 rounded-[100%] disabled:opacity-50"
            >
              <ChevronRightIcon fillColor={currentPage === totalPages ? 'gray' : theme === 'dark' ? 'white' : 'black'} />
            </button>
          </div>
        </div>
      </div>
    )}
  </div>;
};

export default TraderStakesHistory;